import { useCallback, useState } from "react";

import { UpdateCacheAction } from "@/components/cached-messages/types";
import { useNotifications } from "@/contexts/NotificationsContext";
import { formatUnits } from "@/utils/utils";

import { useApi } from "../useApi";
import { getPastTenseAction } from "./utils";

/** One entry the backend reported it could not change, with its reason. */
export interface CachedMessageActionFailure {
  id: string;
  error: string;
}

/**
 * What a cache/uncache request actually did, per entry.
 *
 * Callers need this per id rather than as a single boolean because the endpoint
 * is a batch: some entries can change while others fail in the same request, and
 * a caller updating its own view has to know which rows moved.
 */
export interface CachedMessageActionOutcome {
  /** Entries that are now in the requested state. */
  settled: string[];
  /** Entries that are not, each with the backend's reason. */
  failed: CachedMessageActionFailure[];
}

/**
 * The body `PATCH /admin/cached-messages` answers with.
 *
 * Every field is optional because the response is read defensively: the handler
 * answers `200` on paths that populate different subsets of it (`clear` returns
 * counts with no `details`), and a missing field must not be read as a success.
 */
interface UpdateCachedMessagesResponse {
  message?: string;
  success_count?: number;
  error_count?: number;
  details?: {
    entry_id?: string;
    result?: { status?: string; message?: string };
  }[];
  errors?: { entry_id?: string; error?: string }[];
}

/**
 * Per-entry statuses that mean the entry reached the state the caller asked for.
 *
 * `skipped` is deliberately in here. The handler returns it when a cache found
 * the entry already indexed, or an uncache found it already gone -- and in both
 * cases it has already written the requested `cacheStatus` before returning. The
 * batch helper historically counted those in `error_count`, so reading the counts
 * instead of these statuses would report a completed no-op as a failure and leave
 * the row showing the wrong state.
 */
const SETTLED_STATUSES = new Set(["success", "skipped"]);

const NO_RESPONSE_REASON = "The server returned no result for this entry.";
const UNKNOWN_REASON = "Unknown error.";

/**
 * Work out which entries changed, from the response body rather than from the
 * fact that a response arrived at all.
 *
 * The endpoint answers `200` even when every entry failed, so truthiness of the
 * response says nothing. Prefer the per-entry `details`; fall back to the counts
 * only when `details` is absent, and treat any error count in that case as
 * covering the whole request, since there is nothing to attribute it to.
 */
const readOutcome = (
  response: UpdateCachedMessagesResponse | null,
  messageIds: string[]
): CachedMessageActionOutcome => {
  if (!response) {
    return {
      settled: [],
      failed: messageIds.map((id) => ({ id, error: NO_RESPONSE_REASON })),
    };
  }

  const details = response.details ?? [];
  if (details.length > 0) {
    const settled: string[] = [];
    const failed: CachedMessageActionFailure[] = [];

    details.forEach(({ entry_id: entryId, result }) => {
      const id = entryId ?? "";
      if (SETTLED_STATUSES.has(result?.status ?? "")) {
        settled.push(id);
      } else {
        failed.push({ id, error: result?.message || UNKNOWN_REASON });
      }
    });

    return { settled, failed };
  }

  if ((response.error_count ?? 0) > 0) {
    const reason = response.errors?.[0]?.error || UNKNOWN_REASON;
    return { settled: [], failed: messageIds.map((id) => ({ id, error: reason })) };
  }

  return { settled: messageIds, failed: [] };
};

export const useAdminCachedMessageActions = () => {
  const [loading, setLoading] = useState(false);
  const { notifySuccess, notifyError } = useNotifications();

  const { apiPatch } = useApi({ isAdmin: true });

  const updateCachedMessages = useCallback(
    async (
      action: UpdateCacheAction,
      messageIds: string[] = []
    ): Promise<CachedMessageActionOutcome> => {
      setLoading(true);
      try {
        const response = await apiPatch<UpdateCachedMessagesResponse>(
          `/admin/cached-messages`,
          {
            action,
            messageIds:
              action === UpdateCacheAction.CLEAR ? undefined : messageIds,
          }
        );

        const outcome = readOutcome(response, messageIds);

        // `clear` names no entries, so its outcome cannot be per id; its counts
        // are the only thing to report on.
        const isClear = action === UpdateCacheAction.CLEAR;
        const settledCount = isClear
          ? response?.success_count ?? 0
          : outcome.settled.length;
        const failedCount = isClear
          ? response?.error_count ?? 0
          : outcome.failed.length;

        if (settledCount > 0) {
          notifySuccess(
            `${settledCount} ${formatUnits(settledCount, "message")} ${
              settledCount === 1 ? "has" : "have"
            } been ${getPastTenseAction(action)} successfully.`
          );
        }

        if (failedCount > 0) {
          // The backend's own reason is carried through rather than replaced by a
          // generic "failed" line. A generic toast is what let a total failure --
          // every entry denied by IAM -- read as a success for weeks.
          const reason =
            outcome.failed[0]?.error ??
            response?.errors?.[0]?.error ??
            UNKNOWN_REASON;
          notifyError(
            "Cached Message Action Error.",
            `Failed to ${action} ${failedCount} ${formatUnits(
              failedCount,
              "message"
            )}: ${reason}`
          );
        }

        return outcome;
      } catch (error) {
        console.error(error);
        const reason =
          error instanceof Error && error.message ? error.message : UNKNOWN_REASON;
        notifyError(
          "Cached Messages Error.",
          `Failed to ${action} the ${formatUnits(
            messageIds.length,
            "message"
          )}.`
        );
        return {
          settled: [],
          failed: messageIds.map((id) => ({ id, error: reason })),
        };
      } finally {
        setLoading(false);
      }
    },
    [apiPatch, notifyError, notifySuccess]
  );

  const cacheMessages = useCallback(
    (messageIds: string | string[]) =>
      updateCachedMessages(
        UpdateCacheAction.CACHE,
        Array.isArray(messageIds) ? messageIds : [messageIds]
      ),
    [updateCachedMessages]
  );

  const uncacheMessages = useCallback(
    (messageIds: string | string[]) =>
      updateCachedMessages(
        UpdateCacheAction.UNCACHE,
        Array.isArray(messageIds) ? messageIds : [messageIds]
      ),
    [updateCachedMessages]
  );

  const clearCache = useCallback(
    () => updateCachedMessages(UpdateCacheAction.CLEAR),
    [updateCachedMessages]
  );

  return {
    cacheMessages,
    uncacheMessages,
    clearCache,
    loading,
  };
};
